import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { Dictionary } from '@ngrx/entity';

import { ISoftUpdate } from 'app/models/softUpdate';
import { IUnits } from 'app/models/units';
import { IBar } from 'app/models/bar';
import { IBeverage } from 'app/models/beverage';
import { IBrewfather } from 'app/models/brewfather';
import { ILocation } from 'app/models/location';
import { IFermenter, IFermenterVM, FermenterType } from 'app/models/fermenter';
import { UnitsHelper } from 'app/services/units.helper';
import { ToastService } from 'app/services/toast.service';
import { ColorHelper } from 'app/services/color.helper';
import { BrewfatherExternalService } from 'app/services/external/brewfather.service';
import { SelectBeverageComponent } from 'app/components/select-beverage/select-beverage.component';
import { BeerDetailPage } from 'app/pages/inventory/beer/beer-detail/beer-detail.page';
import { FermenterSvgComponent } from 'app/pages/home/taps/fermenter-svg/fermenter-svg.component';
import { BatchPickerComponent } from '../batch-picker/batch-picker.component';
import { FermentationChartComponent, IBfReading } from '../fermentation-chart/fermentation-chart.component';

@Component({
  selector: 'app-fermenter-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IonicModule, FormsModule, ReactiveFormsModule, FermenterSvgComponent, FermentationChartComponent]
})
export class FormComponent implements OnChanges {

  @Input() item!: IFermenterVM;
  @Input() bar!: IBar;
  @Input() allBeverages!: Dictionary<IBeverage>;
  @Input() allLocations!: ILocation[];
  @Input() units!: IUnits;
  @Input() uid!: string;
  /** Brewfather credentials — undefined until set up; hides the whole section. */
  @Input() brewfather?: IBrewfather;

  @Output() softUpdateFermenter = new EventEmitter<ISoftUpdate<IFermenter>>();

  bfLoading = signal(false);
  /** All device readings for the linked batch — feeds the fermentation chart. */
  bfReadings = signal<IBfReading[]>([]);
  /** Guards the automatic once-per-batch readings fetch on page load. */
  private bfFetchedForBatchId?: string | null;

  types: { value: FermenterType; label: string }[] = [
    { value: 'Grainfather', label: 'Grainfather' },
    { value: 'Conical', label: 'Conical (stainless)' },
    { value: 'Bucket', label: 'Bucket' },
    { value: 'Carboy', label: 'Carboy' },
    { value: 'PressurePET', label: 'Pressure / PET' },
    { value: 'Kegmenter', label: 'Kegmenter' },
  ];

  form = this.fb.group({
    name: ['', { validators: [Validators.required] }],
    description: [''],
    type: ['Conical', { validators: [Validators.required] }],
    size: ['', { validators: [Validators.required] }],
    amount: ['', { validators: [Validators.required] }],
    location: [''],
  });

  beverage!: IBeverage | undefined;

  constructor(
    private fb: FormBuilder,
    private unitsHelper: UnitsHelper,
    private toastService: ToastService,
    private colorHelper: ColorHelper,
    private modalController: ModalController,
    private brewfatherExternalService: BrewfatherExternalService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (this.allBeverages && this.item?.beverageId) {
      this.beverage = this.allBeverages[this.item.beverageId];
    } else {
      this.beverage = undefined;
    }

    if (changes['item']?.currentValue) {
      this.patchFormFromItem(this.item);
    }

    // Fetch fresh Brewfather readings once per linked batch when the page loads.
    if (this.brewfather && this.item?.brewfatherBatchId
      && this.bfFetchedForBatchId !== this.item.brewfatherBatchId) {
      this.refreshBrewfather();
    }
  }

  updateName() {
    if (this.form.controls.name.invalid) {
      this.toastService.error('Name is required');
      return;
    }
    if (this.form.controls.name.value === this.item.name) return;
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { name: this.form.controls.name.value ?? '' });
    }
  }

  updateDescription() {
    if (this.form.controls.description.value === this.item.description) return;
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { description: this.form.controls.description.value ?? '' });
    }
  }

  /** Picking a vessel image saves the type so the graphic updates immediately (no dropdown). */
  setType(type: FermenterType) {
    if (!type) return;
    this.form.patchValue({ type });
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { type });
    }
  }

  updateSize() {
    if (this.form.controls.size.invalid) {
      this.toastService.error('Size is required');
      return;
    }
    const size = +(this.form.controls.size.value ?? '0');
    const value = this.unitsHelper.toUnit(size, this.units.kegs, 'l', -1);
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { size: value });
    }
  }

  updateAmount() {
    if (this.form.controls.amount.invalid) {
      this.toastService.error('Amount is required');
      return;
    }
    const amount = +(this.form.controls.amount.value ?? '0');
    const value = this.unitsHelper.toUnit(amount, this.units.kegs, 'l', -1);
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { amount: value });
    }
  }

  updateLocation(event: any) {
    const locationId = event.detail.value;
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { locationId });
    }
  }

  async deleteBeverage() {
    if (this.item?.id) {
      this.softUpdateCurrent(this.item.id, { beverageId: null });
    }
  }

  async openBeer(id: string) {
    const modal = await this.modalController.create({
      component: BeerDetailPage,
      componentProps: { isModal: true, itemId: id }
    });
    return await modal.present();
  }

  public async openBeverageModal() {
    const modal = await this.modalController.create({
      component: SelectBeverageComponent,
    });

    modal.onDidDismiss().then(async (data) => {
      if (!data.data?.item) return;
      const beverageData = data.data.item as IBeverage;
      if (!beverageData || !this.item.id) return;
      this.softUpdateCurrent(this.item.id, { beverageId: beverageData.id });
    });

    return await modal.present();
  }

  //#region Brewfather

  async openBatchPicker() {
    const modal = await this.modalController.create({
      component: BatchPickerComponent,
      componentProps: { brewfather: this.brewfather },
    });

    modal.onDidDismiss().then((data) => {
      const batch = data.data?.batch;
      if (!batch?._id || !this.item?.id) return;
      this.bfFetchedForBatchId = undefined; // readings fetch re-arms for the new batch
      this.softUpdateCurrent(this.item.id, {
        brewfatherBatchId: batch._id,
        brewfatherBatchName: this.batchDisplayName(batch),
        fermentationStage: batch.status ?? null,
      });
    });

    return await modal.present();
  }

  unlinkBatch() {
    if (!this.item?.id) return;
    this.bfFetchedForBatchId = undefined;
    this.bfReadings.set([]);
    this.softUpdateCurrent(this.item.id, {
      brewfatherBatchId: null,
      brewfatherBatchName: null,
      fermentationStage: null,
      originalGravity: null,
      currentGravity: null,
      finalGravity: null,
      temperature: null,
      color: null,
    });
  }

  /** Apparent attenuation progress: how far SG has travelled from OG towards the FG target. */
  attenuationProgress(): number | null {
    const og = this.item?.originalGravity;
    const fg = this.item?.finalGravity;
    const sg = this.item?.currentGravity;
    if (!og || !fg || !sg || og <= fg) return null;
    const pct = Math.round(((og - sg) / (og - fg)) * 100);
    return Math.min(100, Math.max(0, pct));
  }

  /** Pulls the batch (status/OG/FG/volume/colour) + all device readings and persists
   *  the snapshot on the fermenter, so lists/home can show it without an API call.
   *  While a batch is linked, Brewfather replaces the beverage as the data source:
   *  amount comes from measuredBatchSize and the vessel colour from the recipe SRM. */
  async refreshBrewfather() {
    const item = this.item;
    const batchId = item?.brewfatherBatchId;
    if (!this.brewfather || !item?.id || !batchId || this.bfLoading()) return;
    this.bfFetchedForBatchId = batchId;
    this.bfLoading.set(true);
    try {
      const [batchResult, readingsResult] = await Promise.all([
        this.brewfatherExternalService.getBatch(this.brewfather, batchId),
        this.brewfatherExternalService.getReadings(this.brewfather, batchId),
      ]);
      const batch = this.parseBfResult(batchResult);
      const readings = this.parseBfResult(readingsResult);

      const sorted: IBfReading[] = Array.isArray(readings)
        ? [...readings].filter(r => r?.time != null).sort((a, b) => a.time - b.time)
        : [];
      this.bfReadings.set(sorted);
      const lastReading = [...sorted].reverse().find(r => r.sg != null);

      const update: Partial<IFermenter> = {};
      if (batch) {
        if (batch.status) update.fermentationStage = batch.status;
        const og = batch.measuredOg ?? batch.estimatedOg;
        if (og) update.originalGravity = og;
        const fg = batch.measuredFg ?? batch.estimatedFg;
        if (fg) update.finalGravity = fg;
        const name = this.batchDisplayName(batch);
        if (name) update.brewfatherBatchName = name;
        const srm = batch.recipe?.color;
        if (srm != null) update.color = this.colorHelper.convertSrmToWebColor(srm);
        // Volume into the fermenter — Brewfather reports litres, our base unit.
        const batchSize = batch.measuredBatchSize ?? batch.recipe?.batchSize;
        const currentAmountLitres = this.unitsHelper.toUnit(item.amount ?? 0, this.units.kegs, 'l', -1);
        if (batchSize != null && Math.abs(batchSize - currentAmountLitres) > 0.01) {
          update.amount = batchSize;
        }
      }
      if (lastReading) {
        if (lastReading.sg != null) update.currentGravity = lastReading.sg;
        if (lastReading.temp != null) update.temperature = lastReading.temp;
      }

      // amount is pre-checked against the converted value above; the other keys
      // compare directly (item carries them unconverted).
      const changed = (Object.keys(update) as (keyof IFermenter)[])
        .some(key => key === 'amount' || update[key] !== item[key]);
      if (changed) {
        this.softUpdateCurrent(item.id, update);
      }
    } catch {
      this.toastService.error('Unable to get batch data from Brewfather.');
    } finally {
      this.bfLoading.set(false);
    }
  }

  displayTemperature(temp: number | null | undefined): string {
    if (temp == null || !this.units?.temperature) return '';
    const value = this.unitsHelper.getTemperature(this.units.temperature, temp);
    return `${Math.round(value * 10) / 10} ${this.unitsHelper.getTemperatureUnit(this.units.temperature)}`;
  }

  private batchDisplayName(batch: any): string {
    return batch.name === 'Batch' && batch.recipe?.name ? batch.recipe.name : batch.name ?? '';
  }

  /** Callable results wrap the Brewfather body as a JSON string; errors aren't JSON. */
  private parseBfResult(result: any): any | null {
    try {
      const parsed = JSON.parse(result?.data);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  //#endregion Brewfather

  /** Thumbnail VM for a type option — current beverage colour, fixed representative
   *  fill so every vessel shape reads clearly in the picker regardless of amount. */
  typePreview(type: FermenterType): IFermenterVM {
    return { ...(this.item ?? {}), type, size: 1, amount: 0.6 } as IFermenterVM;
  }

  private patchFormFromItem(item: IFermenterVM) {
    this.form.patchValue({
      name: item.name,
      description: item.description ?? '',
      type: (item.type as FermenterType) ?? 'Conical',
      size: item.size != null ? item.size.toString() : '',
      amount: item.amount != null ? item.amount.toString() : '',
      location: item.locationId ?? '',
    });
  }

  private softUpdateCurrent(id: string, data: Partial<IFermenter>) {
    this.softUpdateFermenter.emit({ id, data, uid: this.uid });
  }
}
